"use client";
import {
  useResizeObserver,
  useScrollIntoView,
  useViewportSize,
} from "@mantine/hooks";
import Icon from "@/elements/icons";
import * as styles from "./styles.css";
import Button from "@/components/button";
import { stegaClean } from "@sanity/client/stega";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";

export default function QuickLinks(props: any) {
  const [data, setData] = useState<any>();
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [container, rect] = useResizeObserver();
  const { width } = useViewportSize();

  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    offset: 180,
    duration: 300,
  });
  const height = useMemo(() => {
    return rect.height + 48 + 56;
  }, [rect]);

  useEffect(() => {
    const parent = container.current.parentNode;

    if (width > 1280) {
      if (parent.children[1]) {
        parent.children[1].style.marginTop = `-${height}px`;
      }
    } else {
      if (parent?.children[1]) {
        parent.children[1].style.marginTop = 0;
      }
    }
  }, [height, container, width]);

  useEffect(() => {
    const headers = document.querySelectorAll(
      ".content__container-tier3 h2, .content__container-tier3 h3, .tier4__content h2, .tier4__content h3"
    );
    if (headers) {
      const textHeaders = [];
      for (let i = 0; i < headers.length; i += 1) {
        if (i === 0) {
          textHeaders.push({
            text: "Overview",
            id: "overview",
          });
          headers[i].setAttribute("id", "overview");
        } else {
          const id = headers[i].innerHTML
            .toLowerCase()
            .replace(/[^A-Z0-9]/gi, "-");
          textHeaders.push({
            text: headers[i].innerHTML,
            id,
          });
          headers[i].setAttribute("id", id);
        }
      }
      setData(textHeaders);

      if (window.location.hash) {
        setActiveId(window.location.hash.substring(1));
      } else {
        if (textHeaders[0]?.id) {
          setActiveId(textHeaders[0].id);
        }
      }
    }
  }, []);

  const clickHandler = () => {
    setOpen(!open);
  };

  const quicklinksHandler = (event: any, id: string) => {
    event.preventDefault();

    clickHandler();

    const targetElement = container.current.parentNode.querySelector(`#${id}`);

    if (targetElement) {
      setActiveId(id);
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  return (
    <div
      ref={container}
      className={`${styles.container}${open ? " is--open" : " is--closed"}${data ? " show" : " hide"}`}
    >
      <div className={styles.heading} onClick={() => clickHandler()}>
        {props.character && props.character?.imageSource && (
          <div className={styles.imageContainer}>
            <Image
              src={`${props.character.imageSource.url}?h=48&q=90&auto=format`}
              alt={props.character.imageSource.imageAlt ?? ""}
              fill={true}
              style={{ objectFit: "cover" }}
              placeholder="blur"
              blurDataURL={`${props.character.imageSource?.lqip ?? `${props.character.imageSource.url}?w=10&auto=format`}`}
            />
          </div>
        )}
        <h6>Quick Links</h6>
        <div className={styles.iconContainer}>
          {!open && <Icon type="chevron-up-filled" />}
          {open && <Icon type="chevron-down-filled" />}
        </div>
      </div>
      {data && (
        <ul className={styles.list}>
          {data.map((d: any, index: number) => (
            <li key={`${d}-${index}`}>
              <a
                href={`#${d.id}`}
                className={`${activeId === d.id ? "is--active" : ""} line-clamp-1`}
                onClick={(event) => quicklinksHandler(event, d.id)}
              >
                {d?.text}
              </a>
            </li>
          ))}
        </ul>
      )}

      {props?.buttonPrimary && props?.buttonPrimary?.buttonVisible && (
        <Button
          href={props.buttonPrimary.buttonLink || ""}
          label={props.buttonPrimary?.buttonLabel || ""}
          style={stegaClean(props.buttonPrimary.buttonVariant) || "primary"}
          target={
            stegaClean(props.buttonPrimary.linkType) === "external link"
              ? "_blank"
              : ""
          }
          size="small"
          rollerProductID={props?.buttonPrimary?.rollerID}
          linkType={props?.buttonPrimary?.linkType}
        />
      )}
    </div>
  );
}
